import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faBolt, faCheck, faFire, faTrophy,
  faPlus, faBullseye, faCalendarDay, faRotateLeft, faChevronRight,
  faStar, faLightbulb, faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { AREA_CONFIG } from './LevelUpHubPage';
import api from '../api/API';
import './PlanejamentoDiaPDIPage.css';

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

const STEP_LABELS = ['Foco', 'Tarefas', 'Confirmar'];

const PlanejamentoDiaPDIPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0=foco, 1=tarefas, 2=confirmar
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [diaAtual, setDiaAtual] = useState(null);

  // Data
  const [metas, setMetas] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [diaTaskIds, setDiaTaskIds] = useState([]); // tarefas já no dia

  // Selections
  const [metaFoco, setMetaFoco] = useState(null); // meta selecionada para o dia
  const [tasksSelecionadas, setTasksSelecionadas] = useState([]); // ids das tasks escolhidas
  const [reflexao, setReflexao] = useState('');
  const [novaTask, setNovaTask] = useState('');

  // UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [filterText, setFilterText] = useState('');

  const ymd = useMemo(() => toYMD(selectedDate), [selectedDate]);
  const isToday = useMemo(() => isSameDay(selectedDate, new Date()), [selectedDate]);

  const selectedCfg = useMemo(
    () => metaFoco ? AREA_CONFIG.find(a => a.id === metaFoco.area) || AREA_CONFIG[0] : null,
    [metaFoco]
  );

  // Load data whenever date changes
  useEffect(() => {
    setLoading(true);
    setSaved(false);
    setStep(0);
    setMetaFoco(null);
    setTasksSelecionadas([]);
    setReflexao('');

    Promise.all([
      api.get('/pdi/metas'),
      api.get('/tasks'),
      api.get(`/dias/por-data/${ymd}`),
    ]).then(async ([metasRes, tasksRes, diaRes]) => {
      const ativas = (metasRes?.data?.records || []).filter(m => !m.concluida);
      setMetas(ativas);
      setAllTasks(tasksRes?.data || []);

      const dia = diaRes?.data;
      setDiaAtual(dia);

      if (dia?.id) {
        const diaTasksRes = await api.get(`/dias/${dia.id}/tarefas`);
        const ids = (diaTasksRes?.data || []).filter(t => t.tipo !== 3).map(t => t.id_tarefa);
        setDiaTaskIds(ids);
        setTasksSelecionadas(ids);
        setReflexao(dia.reflexao || '');

        // Se já tem planejamento, vai para confirmação
        if (ids.length > 0) {
          setSaved(true);
          setStep(2);
        }
      }
    }).catch(console.error).finally(() => setLoading(false));
  }, [ymd]);

  const metasAtivas = useMemo(() => metas.filter(m => !m.concluida), [metas]);

  // Tasks filtradas (da meta ou todas) excluindo já concluídas
  const tasksDisponiveis = useMemo(() => {
    const base = allTasks.filter(t => !t.concluido && t.ativo !== false);
    if (!filterText.trim()) return base;
    const q = filterText.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return base.filter(t =>
      (t.descricao || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(q)
    );
  }, [allTasks, filterText]);

  const tasksMetaFoco = useMemo(() => {
    if (!metaFoco) return [];
    return tasksDisponiveis.filter(t => t.id_meta_pdi === metaFoco.id);
  }, [tasksDisponiveis, metaFoco]);

  const tasksOutras = useMemo(() => {
    if (!metaFoco) return tasksDisponiveis;
    return tasksDisponiveis.filter(t => t.id_meta_pdi !== metaFoco.id);
  }, [tasksDisponiveis, metaFoco]);

  const toggleTask = useCallback((id) => {
    setTasksSelecionadas(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  const xpPotencial = useMemo(() => {
    return tasksSelecionadas.length * 10 + (metaFoco ? 15 : 0);
  }, [tasksSelecionadas, metaFoco]);

  const handleSave = async () => {
    if (!diaAtual?.id) return;
    setSaving(true);
    try {
      // Salva reflexão
      if (reflexao.trim()) {
        await api.put(`/dias/${diaAtual.id}/reflexao`, { reflexao });
      }

      // Sync tarefas do dia: remove antigas, adiciona novas
      const paraRemover = diaTaskIds.filter(id => !tasksSelecionadas.includes(id));
      const paraInserir = tasksSelecionadas.filter(id => !diaTaskIds.includes(id));

      for (const id of paraRemover) {
        await api.delete(`/dias/${diaAtual.id}/tarefas/${id}`);
      }
      for (const id of paraInserir) {
        await api.post(`/dias/${diaAtual.id}/tarefas`, { id_tarefa: id });
      }

      // Atualiza id_meta_pdi nas tasks selecionadas que não têm (quick-link)
      if (metaFoco) {
        for (const id of paraInserir) {
          const task = allTasks.find(t => t.id === id);
          if (task && !task.id_meta_pdi) {
            await api.put(`/tasks/${id}`, { fields: { id_meta_pdi: metaFoco.id } }).catch(() => {});
          }
        }
      }

      setDiaTaskIds(tasksSelecionadas);
      setSaved(true);
      setStep(2);
    } catch (err) {
      console.error('[PDI Dia] Erro ao salvar:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCriarTask = async () => {
    if (!novaTask.trim()) return;
    try {
      const res = await api.post('/tasks', {
        fields: {
          descricao: novaTask.trim(),
          id_meta_pdi: metaFoco?.id || null,
          id_dia: diaAtual?.id || null,
          ativo: true,
          concluido: false,
          tipo: 0,
        }
      });
      const created = res?.data;
      if (created?.id) {
        setAllTasks(prev => [...prev, created]);
        setTasksSelecionadas(prev => [...prev, created.id]);
      }
      setNovaTask('');
    } catch (err) {
      console.error('[PDI Dia] Erro ao criar task:', err);
    }
  };

  const formatDate = (date) => date.toLocaleDateString('pt-BR', {
    weekday: 'long', day: 'numeric', month: 'long'
  });

  if (loading) {
    return (
      <div className="pdia-page">
        <div className="pdia-loading">
          <div className="pdia-loading-spinner" />
          <p>Carregando planejamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pdia-page">
      <div className="pdia-orb pdia-orb-1" />
      <div className="pdia-orb pdia-orb-2" />

      {/* Header */}
      <div className="pdia-header">
        <div className="pdia-header-left">
          <button className="pdia-back-btn" onClick={() => navigate('/levelup')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            LevelUp Hub
          </button>
          <div className="pdia-date-nav">
            <button className="pdia-nav-btn" onClick={() => setSelectedDate(d => addDays(d, -1))}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div className="pdia-date-info">
              <h1 className="pdia-date-label">
                <FontAwesomeIcon icon={faCalendarDay} style={{ marginRight: 10, color: '#6366f1' }} />
                {isToday ? 'Hoje' : formatDate(selectedDate)}
              </h1>
              {!isToday && (
                <button className="pdia-today-btn" onClick={() => setSelectedDate(new Date())}>
                  Ir para hoje
                </button>
              )}
            </div>
            <button className="pdia-nav-btn" onClick={() => setSelectedDate(d => addDays(d, 1))}>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>

        <div className="pdia-xp-pill">
          <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b' }} />
          <span>{xpPotencial} XP potencial hoje</span>
        </div>
      </div>

      {/* Steps */}
      <div className="pdia-steps">
        {STEP_LABELS.map((label, i) => (
          <React.Fragment key={i}>
            <div
              className={`pdia-step ${i === step ? 'active' : ''} ${i < step || saved ? 'done' : ''}`}
              onClick={() => !saving && setStep(i)}
            >
              <div className="pdia-step-dot">
                {(i < step || (saved && i < 2)) ? <FontAwesomeIcon icon={faCheck} /> : i + 1}
              </div>
              <span>{label}</span>
            </div>
            {i < 2 && <div className={`pdia-step-line ${i < step ? 'done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      <div className="pdia-content">

        {/* STEP 0 — Foco da Meta */}
        {step === 0 && (
          <div className="pdia-step-panel">
            <div className="pdia-step-header">
              <FontAwesomeIcon icon={faBullseye} className="pdia-step-icon" style={{ color: '#6366f1' }} />
              <div>
                <h2>Qual objetivo você avança hoje?</h2>
                <p>Escolha a meta que guiará suas escolhas de tarefa hoje</p>
              </div>
            </div>

            {metasAtivas.length === 0 ? (
              <div className="pdia-empty">
                <FontAwesomeIcon icon={faTrophy} className="pdia-empty-icon" />
                <p>Nenhuma meta ativa ainda.</p>
                <button className="pdia-empty-cta" onClick={() => navigate('/metas')}>
                  Criar primeira meta →
                </button>
              </div>
            ) : (
              <div className="pdia-meta-grid">
                {metasAtivas.map(meta => {
                  const cfg = AREA_CONFIG.find(a => a.id === meta.area) || AREA_CONFIG[0];
                  const isSelected = metaFoco?.id === meta.id;
                  return (
                    <div
                      key={meta.id}
                      className={`pdia-meta-card ${isSelected ? 'selected' : ''}`}
                      style={{ '--meta-color': cfg.color, '--meta-glow': cfg.glow }}
                      onClick={() => setMetaFoco(isSelected ? null : meta)}
                    >
                      <div className="pdia-meta-card-top">
                        <div
                          className="pdia-meta-area-icon"
                          style={{ background: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}44` }}
                        >
                          <FontAwesomeIcon icon={cfg.icon} />
                        </div>
                        {isSelected && (
                          <div className="pdia-meta-check">
                            <FontAwesomeIcon icon={faCircleCheck} />
                          </div>
                        )}
                      </div>
                      <p className="pdia-meta-titulo">{meta.titulo}</p>
                      <span className="pdia-meta-tipo">{meta.tipo}</span>
                      <div className="pdia-meta-progress-bar">
                        <div style={{ width: `${meta.progresso || 0}%`, background: cfg.color }} />
                      </div>
                      <span className="pdia-meta-pct">{meta.progresso || 0}% completa</span>
                      {Number(meta.tarefas_total) > 0 && (
                        <span className="pdia-meta-task-count">
                          {meta.tarefas_concluidas}/{meta.tarefas_total} tarefas
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pdia-step-footer">
              <div className="pdia-reflexao-field">
                <label>
                  <FontAwesomeIcon icon={faLightbulb} style={{ color: '#f59e0b', marginRight: 6 }} />
                  Intenção do dia — o que precisa acontecer para você sentir que avançou?
                </label>
                <textarea
                  className="pdia-reflexao-input"
                  value={reflexao}
                  onChange={e => setReflexao(e.target.value)}
                  placeholder="Ex: Fechar o módulo de autenticação, correr 5km, ligar para o João..."
                  rows={3}
                />
              </div>
              <button
                className="pdia-btn-primary"
                onClick={() => setStep(1)}
              >
                Escolher Tarefas
                <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: 8 }} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — Tarefas */}
        {step === 1 && (
          <div className="pdia-step-panel">
            <div className="pdia-step-header">
              <FontAwesomeIcon icon={faCheck} className="pdia-step-icon" style={{ color: '#10b981' }} />
              <div>
                <h2>Tarefas para hoje</h2>
                <p>
                  {metaFoco
                    ? `Tarefas vinculadas a "${metaFoco.titulo}" aparecem primeiro`
                    : 'Selecione as tarefas que você vai executar hoje'}
                </p>
              </div>
              <div className="pdia-selected-badge">
                {tasksSelecionadas.length} selecionada{tasksSelecionadas.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Quick add */}
            <div className="pdia-quick-add">
              <input
                className="pdia-quick-input"
                value={novaTask}
                onChange={e => setNovaTask(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCriarTask()}
                placeholder="Nova tarefa rápida... (Enter para adicionar)"
              />
              <button className="pdia-quick-btn" onClick={handleCriarTask}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            <input
              className="pdia-filter-input"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Filtrar tarefas..."
            />

            <div className="pdia-tasks-scroll">
            {/* Tasks da meta em foco */}
            {metaFoco && tasksMetaFoco.length > 0 && (
              <div className="pdia-task-group">
                <div
                  className="pdia-task-group-label"
                  style={{ color: selectedCfg?.color }}
                >
                  <FontAwesomeIcon icon={selectedCfg?.icon} style={{ marginRight: 6 }} />
                  Vinculadas a "{metaFoco.titulo}"
                </div>
                {tasksMetaFoco.map(task => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    selected={tasksSelecionadas.includes(task.id)}
                    onToggle={toggleTask}
                    highlight={selectedCfg?.color}
                  />
                ))}
              </div>
            )}

            {/* Outras tasks */}
            <div className="pdia-task-group">
              {metaFoco && tasksMetaFoco.length > 0 && (
                <div className="pdia-task-group-label" style={{ color: 'rgba(226,232,240,0.4)' }}>
                  Outras tarefas
                </div>
              )}
              {tasksOutras.length === 0 && tasksMetaFoco.length === 0 && (
                <div className="pdia-tasks-empty">
                  Nenhuma tarefa disponível. Crie uma acima.
                </div>
              )}
              {tasksOutras.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  selected={tasksSelecionadas.includes(task.id)}
                  onToggle={toggleTask}
                />
              ))}
            </div>
            </div>

            <div className="pdia-step-footer pdia-step-footer-row">
              <button className="pdia-btn-ghost" onClick={() => setStep(0)}>
                <FontAwesomeIcon icon={faArrowLeft} /> Voltar
              </button>
              <button
                className="pdia-btn-primary"
                onClick={() => setStep(2)}
              >
                Revisar Plano
                <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: 8 }} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — Confirmar */}
        {step === 2 && (
          <div className="pdia-step-panel">
            <div className="pdia-step-header">
              <FontAwesomeIcon icon={faStar} className="pdia-step-icon" style={{ color: '#f59e0b' }} />
              <div>
                <h2>Seu plano para hoje</h2>
                <p>{saved ? 'Planejamento salvo e pronto para execução' : 'Revise e confirme seu plano'}</p>
              </div>
            </div>

            {/* XP Summary */}
            <div className="pdia-summary-xp">
              <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', fontSize: '1.5rem' }} />
              <div>
                <strong>{xpPotencial} XP</strong>
                <small>potencial se concluir tudo hoje</small>
              </div>
            </div>

            {/* Meta foco */}
            {metaFoco && selectedCfg && (
              <div className="pdia-summary-section">
                <h4>Objetivo do dia</h4>
                <div
                  className="pdia-summary-meta"
                  style={{ '--meta-color': selectedCfg.color, '--meta-glow': selectedCfg.glow }}
                >
                  <FontAwesomeIcon icon={selectedCfg.icon} style={{ color: selectedCfg.color, fontSize: '1.2rem' }} />
                  <div>
                    <strong>{metaFoco.titulo}</strong>
                    <small>{selectedCfg.label} · {metaFoco.tipo}</small>
                  </div>
                  <div className="pdia-meta-xp-tag">
                    <FontAwesomeIcon icon={faBolt} />
                    +15 XP foco
                  </div>
                </div>
              </div>
            )}

            {/* Reflexão */}
            {reflexao && (
              <div className="pdia-summary-section">
                <h4>Intenção</h4>
                <div className="pdia-summary-reflexao">"{reflexao}"</div>
              </div>
            )}

            {/* Tarefas selecionadas */}
            <div className="pdia-summary-section">
              <h4>
                Tarefas do dia
                <span className="pdia-task-count-badge">{tasksSelecionadas.length}</span>
              </h4>
              {tasksSelecionadas.length === 0 ? (
                <p className="pdia-summary-empty">Nenhuma tarefa selecionada.</p>
              ) : (
                <div className="pdia-summary-task-list">
                  {tasksSelecionadas.map(id => {
                    const task = allTasks.find(t => t.id === id);
                    if (!task) return null;
                    const isLinked = task.id_meta_pdi === metaFoco?.id;
                    return (
                      <div key={id} className="pdia-summary-task-item">
                        <div className={`pdia-summary-task-dot ${isLinked ? 'linked' : ''}`}
                          style={isLinked ? { background: selectedCfg?.color } : {}} />
                        <span>{task.descricao}</span>
                        {isLinked && (
                          <span className="pdia-linked-badge" style={{ background: `${selectedCfg?.color}22`, color: selectedCfg?.color }}>
                            meta
                          </span>
                        )}
                        <button className="pdia-remove-task" onClick={() => toggleTask(id)} title="Remover">
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="pdia-step-footer pdia-step-footer-row">
              {!saved && (
                <button className="pdia-btn-ghost" onClick={() => setStep(1)}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Editar
                </button>
              )}
              {saved && (
                <button className="pdia-btn-ghost" onClick={() => { setSaved(false); setStep(0); }}>
                  <FontAwesomeIcon icon={faRotateLeft} /> Replanejar
                </button>
              )}
              {!saved ? (
                <button
                  className="pdia-btn-primary pdia-btn-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : (
                    <>
                      <FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: 8 }} />
                      Confirmar Plano
                    </>
                  )}
                </button>
              ) : (
                <button
                  className="pdia-btn-primary"
                  onClick={() => navigate('/dashboard')}
                >
                  <FontAwesomeIcon icon={faFire} style={{ marginRight: 8 }} />
                  Iniciar o Dia →
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Task item component
const PRIORITY_COLORS = { 1: '#ef4444', 2: '#f97316', 3: '#6366f1', 4: '#64748b' };
const PRIORITY_LABELS = { 1: 'Urgente', 2: 'Alta', 3: 'Média', 4: 'Baixa' };

function TaskItem({ task, selected, onToggle, highlight }) {
  const prioColor = PRIORITY_COLORS[task.prioridade] || PRIORITY_COLORS[3];
  return (
    <div
      className={`pdia-task-item ${selected ? 'selected' : ''}`}
      style={selected && highlight ? { '--task-hl': highlight } : {}}
      onClick={() => onToggle(task.id)}
    >
      <div className={`pdia-task-checkbox ${selected ? 'checked' : ''}`}
        style={selected && highlight ? { background: highlight, borderColor: highlight } : {}}>
        {selected && <FontAwesomeIcon icon={faCheck} />}
      </div>
      <div className="pdia-task-info">
        <span className="pdia-task-desc">{task.descricao}</span>
        {task.vencimento && (
          <span className="pdia-task-due">
            Vence {new Date(task.vencimento).toLocaleDateString('pt-BR')}
          </span>
        )}
      </div>
      <div className="pdia-task-priority" style={{ color: prioColor }}>
        {PRIORITY_LABELS[task.prioridade] || ''}
      </div>
    </div>
  );
}

export default PlanejamentoDiaPDIPage;
